import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { FlexField, FlexFieldValue, FlexFieldUpload, FlexFieldUploadsByPaidolIdQuery } from 'API';
import { RootState } from 'app/store/rootReducer';
import { graphqlOperation } from 'aws-amplify';
import {
  flexFieldUploadsByPaidolId,
  deleteFlexField as deleteFlexFieldMutation,
} from 'graphql_custom/flex-fields';
import { graphqlRequest } from 'app/services/middleware';
import { transactionService } from 'app/services/transaction.service';
import { GraphQLResult } from '@aws-amplify/api-graphql';

export interface FlexFieldState {
  loading: boolean;
  fields: FlexField[];
  selectedField: FlexField | null;
  fieldValues: {
    items: FlexFieldValue[];
    nextToken: string | null;
    total: number;
  } | null;
  fieldValuesLoading: boolean;
  uploads: FlexFieldUpload[];
}

export const initialState: FlexFieldState = {
  loading: false,
  fields: [],
  selectedField: null,
  fieldValues: null,
  fieldValuesLoading: false,
  uploads: [],
};

export const fetchFlexFields = createAsyncThunk<FlexField[], string>(
  'flexField/fetchFlexFields',
  async (paidolId: string) => {
    const result = await transactionService.listFlexFieldsByPaidolId({
      paidolId,
      sortDirection: 'ASC',
    });

    return result.items;
  }
);

export const fetchFlexFieldValues = createAsyncThunk<
  { items: FlexFieldValue[]; nextToken: string | null },
  { flexFieldId: string; limit?: number; nextToken?: string }
>('flexField/fetchFlexFieldValues', async ({ flexFieldId, limit = 100, nextToken }) => {
  const result = await transactionService.listFlexFieldValuesByFlexFieldId({
    flexFieldId,
    limit,
    nextToken,
    sortDirection: 'ASC',
  });

  return {
    items: result.items,
    nextToken: result.nextToken || null,
  };
});

export const fetchFlexFieldUploads = createAsyncThunk<FlexFieldUpload[], string>(
  'flexField/fetchFlexFieldUploads',
  async (paidolId: string, { dispatch }) => {
    const result = (await graphqlRequest(
      graphqlOperation(flexFieldUploadsByPaidolId, {
        paidolId,
        sortDirection: 'DESC',
      }),
      dispatch
    )) as GraphQLResult<FlexFieldUploadsByPaidolIdQuery>;

    return (result.data?.flexFieldUploadsByPaidolId?.items as FlexFieldUpload[]) || [];
  }
);

export const createFlexField = createAsyncThunk<
  FlexField,
  { paidolId: string; name: string; source: string; createdBy?: string }
>('flexField/createFlexField', async ({ paidolId, name, source, createdBy }) =>
  transactionService.createFlexField({
    paidolId,
    name,
    source,
    createdBy,
  })
);

export const deleteFlexField = createAsyncThunk<string, string>(
  'flexField/deleteFlexField',
  async (id: string, { dispatch }) => {
    await graphqlRequest(
      graphqlOperation(deleteFlexFieldMutation, {
        input: { id },
      }),
      dispatch
    );
    return id;
  }
);

const flexFieldSlice = createSlice({
  name: 'flexField',
  initialState,
  reducers: {
    setSelectedField: (state, action) => {
      state.selectedField = action.payload;
    },
    clearFieldValues: (state) => {
      state.fieldValues = null;
    },
    resetFlexFieldSlice: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFlexFields.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchFlexFields.fulfilled, (state, action) => {
        state.loading = false;
        state.fields = action.payload;
      })
      .addCase(fetchFlexFields.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchFlexFieldValues.pending, (state) => {
        state.fieldValuesLoading = true;
      })
      .addCase(fetchFlexFieldValues.fulfilled, (state, action) => {
        state.fieldValuesLoading = false;
        const existing = state.fieldValues?.items || [];
        state.fieldValues = {
          items: action.meta.arg.nextToken ? [...existing, ...action.payload.items] : action.payload.items,
          nextToken: action.payload.nextToken,
          total: action.meta.arg.nextToken
            ? existing.length + action.payload.items.length
            : action.payload.items.length,
        };
      })
      .addCase(fetchFlexFieldValues.rejected, (state) => {
        state.fieldValuesLoading = false;
      })
      .addCase(fetchFlexFieldUploads.fulfilled, (state, action) => {
        state.uploads = action.payload;
      })
      .addCase(createFlexField.fulfilled, (state, action) => {
        state.fields.push(action.payload);
      })
      .addCase(deleteFlexField.fulfilled, (state, action) => {
        state.fields = state.fields.filter((f) => f.id !== action.payload);
        if (state.selectedField?.id === action.payload) {
          state.selectedField = null;
          state.fieldValues = null;
        }
      });
  },
});

export const { setSelectedField, clearFieldValues, resetFlexFieldSlice } = flexFieldSlice.actions;

export const selectFlexField = (state: RootState) => state.flexField;

export default flexFieldSlice.reducer;
